import { useQuery } from '@tanstack/react-query';
import { Modal } from 'antd';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { useAuth } from './auth.tsx';
import { WorkspaceIcon, type WorkspaceIconName } from './icons.tsx';
import { getProject, listProjects } from './project-api.ts';
import { readFavoriteProjectIds, toggleFavoriteProject, writeFavoriteProjectIds } from './project-favorites.ts';
import { managementLinksFor } from './page-access.ts';

type IconName = WorkspaceIconName;

const routeLabels: Array<[RegExp, string, string?]> = [
  [/^\/workspace/, '工作台'], [/^\/management-dashboard/, '管理看板'], [/^\/contracts/, '合同档案'], [/^\/operations\/settlement/, '结算'],
  [/^\/operations\/bank-accounts/, '结算', '收款账户管理'], [/^\/me\/payee/, '个人中心', '我的收款信息'],
  [/^\/people/, '基础资料', '人员档案'], [/^\/customers/, '基础资料', '客户档案'],
  [/^\/service-templates/, '基础资料', '服务模板与费率'], [/^\/system\//, '系统管理'],
  [/^\/operations\//, '系统管理'], [/^\/projects\/new/, '项目', '新建项目'], [/^\/projects/, '项目'],
];

const projectModuleLabels: Array<[RegExp, string]> = [
  [/\/packages$/, '服务包'], [/\/content$/, '内容与任务'], [/\/deliveries$/, '交付登记'],
  [/\/publications$/, '发布管理'], [/\/work-expenses$/, '工作与报销'], [/\/changes$/, '变更与异常'],
  [/\/operations$/, '费用记录'], [/\/contracts$/, '关联合同'], [/\/team$/, '成员'], [/\/settings$/, '设置'],
];

interface ProjectTab { to: string; label: string; page: string; end?: boolean }

export function projectTabs(projectId: string, pages: Set<string>, publicationOnly = false): ProjectTab[] {
  return [
    { to: `/projects/${projectId}`, label: '概览', page: 'PROJECTS', end: true },
    { to: `/projects/${projectId}/packages`, label: '服务包', page: 'PROJECT_PACKAGES' },
    { to: `/projects/${projectId}/content`, label: '内容与任务', page: 'PROJECT_CONTENT' },
    { to: `/projects/${projectId}/deliveries`, label: '交付登记', page: 'PROJECT_DELIVERIES' },
    { to: `/projects/${projectId}/publications`, label: '发布管理', page: 'PROJECT_PUBLICATIONS' },
    { to: `/projects/${projectId}/work-expenses`, label: '工作与报销', page: 'PROJECT_WORK_EXPENSES' },
    { to: `/projects/${projectId}/changes`, label: '变更与异常', page: 'PROJECT_CHANGES' },
    { to: `/projects/${projectId}/operations`, label: '费用记录', page: 'PROJECT_FINANCE' },
    { to: `/projects/${projectId}/contracts`, label: '关联合同', page: 'PROJECT_CONTRACTS' },
    { to: `/projects/${projectId}/team`, label: '成员', page: 'PROJECT_MEMBERS' },
    { to: `/projects/${projectId}/settings`, label: '设置', page: 'PROJECT_SETTINGS' },
  ].filter((tab) => pages.has(tab.page) && !(publicationOnly && tab.end));
}

export function ProjectSubnav({ projectId }: { projectId: string }) {
  const { state } = useAuth();
  const pages = new Set(state.kind === 'ready' ? state.session.pageAccess : []);
  const publicationOnly = state.kind === 'ready' &&
    state.session.roleAssignments.some((role) => role.capabilityBaseline === 'PUBLISHER') &&
    !state.session.capabilities.includes('publication.plan');
  const tabs = projectTabs(projectId, pages, publicationOnly);
  return <nav className="project-tabs inline-project-tabs" aria-label="项目导航">{tabs.map((tab) => <NavLink key={tab.to}
    to={tab.to} {...(tab.end ? { end: true } : {})} className={({ isActive }) => isActive ? 'is-active' : ''}>
    {tab.label}</NavLink>)}</nav>;
}

export function WorkspaceShell({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const match = location.pathname.match(/^\/projects\/([^/]+)(?:\/|$)/);
  const projectId = match?.[1] && match[1] !== 'new' ? match[1] : null;
  const layoutParams = new URLSearchParams(location.search);
  const queryLayout = layoutParams.get('layout') ?? layoutParams.get('view');
  const storedLayout = typeof window === 'undefined' ? null : window.localStorage.getItem('youkuai.project-module-layout');
  const moduleLayout = queryLayout === 'split' || queryLayout === 'table' ? queryLayout :
    storedLayout === 'split' || storedLayout === 'table' ? storedLayout : 'table';
  // Business modules in split mode still use their legacy heading, so the shell supplies
  // only their tabs. Overview and settings own the same compact header used by table pages.
  const shellOwnsSplitProjectTabs = !!projectId && moduleLayout === 'split' &&
    location.pathname !== `/projects/${projectId}` && location.pathname !== `/projects/${projectId}/settings`;
  const token = auth.state.kind === 'ready' ? auth.state.token : '';
  const accountId = auth.state.kind === 'ready' ? auth.state.session.userId : '';
  const [favoriteProjectIds, setFavoriteProjectIds] = useState<string[]>([]);
  const recent = useQuery({ queryKey: ['shell-recent-projects', accountId], queryFn: () => listProjects(token),
    enabled: auth.state.kind === 'ready' && !!accountId && auth.state.session.pageAccess.includes('PROJECTS') });
  const currentProject = useQuery({ queryKey: ['project', accountId, projectId], queryFn: () => getProject(token, projectId!),
    enabled: auth.state.kind === 'ready' && !!accountId && !!projectId });
  useEffect(() => {
    setFavoriteProjectIds(readFavoriteProjectIds(typeof window === 'undefined' ? null : window.localStorage, accountId));
  }, [accountId]);
  if (auth.state.kind !== 'ready') return <>{children}</>;

  const pages = new Set(auth.state.session.pageAccess);
  const roles = auth.state.session.roleAssignments;
  const priority = ['SYSTEM_ADMIN', 'MANAGER', 'FINANCE', 'CONFIRMER', 'PLANNER', 'EXECUTOR', 'READ_ONLY'];
  const role = [...roles].sort((a, b) => priority.indexOf(a.capabilityBaseline) - priority.indexOf(b.capabilityBaseline))[0];
  const roleLabel = role?.name ?? '工作成员';
  const breadcrumbs = routeLabels.find(([pattern]) => pattern.test(location.pathname));
  const canCreateProject = auth.state.session.capabilities.includes('project.create');
  const recentItems = recent.data?.items.slice(0, 3) ?? [];
  const projectItems = new Map((recent.data?.items ?? []).map((item) => [item.projectId, item]));
  if (currentProject.data) projectItems.set(currentProject.data.projectId, currentProject.data);
  const favoriteItems = favoriteProjectIds.map((id) => projectItems.get(id)).filter((item) => item !== undefined);
  const currentProjectIsFavorite = !!projectId && favoriteProjectIds.includes(projectId);

  function toggleCurrentProjectFavorite() {
    if (!projectId) return;
    setFavoriteProjectIds((current) => {
      const next = toggleFavoriteProject(current, projectId);
      writeFavoriteProjectIds(typeof window === 'undefined' ? null : window.localStorage, accountId, next);
      return next;
    });
  }

  const primary = [
    pages.has('WORKSPACE') && { to: '/workspace', label: '工作台', icon: 'workspace' as const },
    pages.has('MANAGEMENT_DASHBOARD') && { to: '/management-dashboard', label: '管理看板', icon: 'management' as const },
    pages.has('PROJECTS') && { to: '/projects', label: '项目', icon: 'project' as const },
    pages.has('CONTRACTS') && { to: '/contracts', label: '合同档案', icon: 'contract' as const },
    pages.has('SETTLEMENTS') && { to: '/operations/settlement', label: '结算', icon: 'settlement' as const },
  ].filter(Boolean) as Array<{ to: string; label: string; icon: IconName }>;
  const canManageBankAccounts = pages.has('SETTLEMENTS') && auth.state.session.roles.some((item) =>
    item === 'ADMIN' || item === 'ADMINISTRATION');
  const management = [
    ...(canManageBankAccounts ? [{ page: 'SETTLEMENTS' as const, to: '/operations/bank-accounts', label: '收款账户管理' }] : []),
    ...managementLinksFor(auth.state.session.pageAccess),
  ].map((item) => ({ ...item,
    icon: item.page === 'SYSTEM' ? 'system' as const : 'foundation' as const,
    active: location.pathname.startsWith(item.to),
  }));

  return <div className={`workspace-app ${projectId ? 'is-project-workspace' : ''} ${mobileOpen ? 'is-mobile-open' : ''} ${collapsed ? 'is-sidebar-collapsed' : ''}`}>
    <button className="workspace-mobile-scrim" aria-label="关闭导航" onClick={() => setMobileOpen(false)} />
    <aside className="workspace-sidebar">
      <div className="workspace-sidebar-head"><NavLink to="/" className="workspace-brand" onClick={() => setMobileOpen(false)}>
        <img src="/brand/youkuai-project-mark-128.webp" alt="" width="128" height="128"
          decoding="async" fetchPriority="high" /><span><strong>优快</strong><small>业务运营系统</small></span></NavLink>
        <button className="workspace-collapse" onClick={() => setCollapsed((value) => !value)}
          aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}><WorkspaceIcon name="panel" /></button></div>
      {canCreateProject && <Link className="workspace-new-action" to="/projects/new" onClick={() => setMobileOpen(false)}>
        <WorkspaceIcon name="plus" /><b>新建项目</b><kbd>⌘ N</kbd></Link>}
      <nav className="workspace-primary-nav" aria-label="主导航">{primary.map((item) => <NavLink key={item.to} to={item.to}
        onClick={() => setMobileOpen(false)} className={({ isActive }) => isActive ? 'is-active' : ''}>
        <WorkspaceIcon name={item.icon} /><b>{item.label}</b></NavLink>)}</nav>
      {management.length > 0 && <section className="workspace-nav-section"><span>管理</span><nav>{management.map((item) =>
        <NavLink key={item.to} to={item.to} onClick={() => setMobileOpen(false)} className={item.active ? 'is-active' : ''}>
          <WorkspaceIcon name={item.icon} /><b>{item.label}</b></NavLink>)}</nav></section>}
      {favoriteItems.length > 0 && <section className="workspace-favorites"><span>收藏项目</span>{favoriteItems.map((item) =>
        <Link key={item.projectId} to={`/projects/${item.projectId}`} onClick={() => setMobileOpen(false)}>
          <i><WorkspaceIcon name="pin" /></i><b>{item.name}</b></Link>)}</section>}
      {recentItems.length > 0 && <section className="workspace-recent"><span>最近项目</span>{recentItems.map((item) =>
        <Link key={item.projectId} to={`/projects/${item.projectId}`} onClick={() => setMobileOpen(false)}>
          <i>{item.name.slice(0, 1)}</i><b>{item.name}</b></Link>)}</section>}
      <div className="workspace-sidebar-spacer" />
      <div className="workspace-sidebar-bottom">{pages.has('WORKSPACE') &&
        <Link className="workspace-utility" to="/me/payee" onClick={() => setMobileOpen(false)}>
          <WorkspaceIcon name="settlement" /><b>我的收款信息</b></Link>}
        {pages.has('WORKSPACE') && <Link className="workspace-utility" to="/me/ai-access" onClick={() => setMobileOpen(false)}>
          <WorkspaceIcon name="system" /><b>AI 接入</b></Link>}
        <Link className="workspace-utility" to="/projects" onClick={() => setMobileOpen(false)}>
        <WorkspaceIcon name="search" /><b>项目搜索</b></Link>
        <div className="workspace-profile">
          <i>{auth.state.session.displayName.slice(0, 1)}</i>
          <span><strong>{auth.state.session.displayName}</strong><small>{roleLabel}</small></span>
          <button type="button" className="workspace-signout" title="退出登录" aria-label="退出登录"
            onClick={() => Modal.confirm({
              title: '退出登录？',
              content: '退出后需要重新通过工作账号登录。',
              okText: '退出登录',
              cancelText: '取消',
              okButtonProps: { danger: true },
              onOk: () => auth.signOut(),
            })}>退出</button>
        </div></div>
    </aside>
    <section className="workspace-body"><header className="workspace-topbar">
      <button className="workspace-mobile-menu" onClick={() => setMobileOpen(true)} aria-label="打开导航"><WorkspaceIcon name="menu" /></button>
      <div className="workspace-breadcrumb">{projectId ? <><Link to="/projects">项目</Link><i>/</i><Link to={`/projects/${projectId}`}>{currentProject.data?.name ?? '读取项目…'}</Link>
        {projectModuleLabels.find(([pattern]) => pattern.test(location.pathname))?.[1] && <><i>/</i><strong>{projectModuleLabels.find(([pattern]) => pattern.test(location.pathname))?.[1]}</strong></>}</> :
        <><Link to={breadcrumbs?.[0].source.includes('projects') ? '/projects' : '/workspace'}>{breadcrumbs?.[1] ?? '工作区'}</Link>{breadcrumbs?.[2] && <><i>/</i><strong>{breadcrumbs[2]}</strong></>}</>}</div>
      {projectId && <button type="button" className={`workspace-project-pin${currentProjectIsFavorite ? ' is-active' : ''}`}
        aria-pressed={currentProjectIsFavorite} title={currentProjectIsFavorite ? '取消收藏项目' : '收藏项目'}
        onClick={toggleCurrentProjectFavorite}><WorkspaceIcon name="pin" />
        <span>{currentProjectIsFavorite ? '已收藏' : '收藏项目'}</span></button>}
      <Link to="/projects" className="workspace-global-search"><WorkspaceIcon name="search" />
        <span>进入项目搜索</span></Link>
      <button className="workspace-top-icon" aria-label="当前没有新通知" title="当前没有新通知"><WorkspaceIcon name="bell" /></button>
      <span className="workspace-top-avatar" aria-label={auth.state.session.displayName}>
        {auth.state.session.displayName.slice(0, 1)}</span></header>
      <div className="workspace-content">
        {shellOwnsSplitProjectTabs && <div className="workspace-project-tabs">
          <ProjectSubnav projectId={projectId} /></div>}{children}</div>
    </section>
  </div>;
}
